import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '../../actions/framework';
import type { ActionTrackingContext } from '../../actions/framework/action-tracking';
import { ChatModel } from '../chat.model';
import {
  getCocoConversationId,
  CocoEvent,
  cocoActionMetadata,
  getChatSurface,
} from '@cotera/client/app/etc';
import { z } from 'zod/v3';
import { ok } from 'neverthrow';

export class EditAction extends BaseAction<
  {},
  { t: string; message?: string }[]
> {
  title = 'Edit Message';
  inputSchema = z.object({
    messageId: z.string(),
    newMessage: z.string(),
  });
  group = 'Chat';

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(ChatModel);
  }

  async execute(
    params: { messageId: string; newMessage: string },
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const chatModel = context.targetedModelOfType(ChatModel)!;
    chatModel.chatController.setMessages((currentMessages) => {
      const updatedMessages = [...currentMessages];
      const messageIndex = updatedMessages.findIndex(
        (msg) => msg.id === params.messageId
      );

      if (messageIndex !== -1 && updatedMessages[messageIndex]) {
        const originalMessage = updatedMessages[messageIndex];

        // Create a completely new message object with the correct UIMessage structure
        const newMessage = {
          id: originalMessage.id, // Keep the same ID
          role: originalMessage.role,
          content: params.newMessage, // New content
          parts: [
            {
              type: 'text' as const,
              text: params.newMessage,
            },
          ],
        };

        // Replace the original message with the new one
        updatedMessages[messageIndex] = newMessage;

        // Truncate the conversation to the edited message
        return updatedMessages.slice(0, messageIndex + 1);
      }

      return updatedMessages;
    });

    await chatModel.chatController.regenerate();

    return ok({});
  }

  track({
    context,
  }: ActionTrackingContext<
    { messageId: string; newMessage: string },
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const conversationId = getCocoConversationId(context);
    const chatModel = context.targetedModelOfType(ChatModel);
    if (conversationId === null) {
      return null;
    }

    return cocoActionMetadata(CocoEvent.MessageEdited, {
      conversationId,
      surface: getChatSurface(context),
      model: chatModel?.model ?? null,
    });
  }
}
